import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Loader2, MousePointerClick, Phone, ShoppingBag, Egg, TrendingUp, Trash2 } from "lucide-react";

export default function AdminCustomers() {
  usePageMeta("Customers");
  const [customers, setCustomers] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/customers"), api.get("/admin/analytics/clicks")])
      .then(([c, k]) => {
        setCustomers(c.data);
        setClicks(k.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} (${c.mobile})? This deletes the customer along with their reviews and click history.`)) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success(`${c.name} removed`);
      setCustomers((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const maxClicks = clicks.length ? Math.max(...clicks.map((c) => c.clicks)) : 0;

  return (
    <div>
      <div>
        <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Customers</h1>
        <p className="mt-1 text-ink-soft">Customer details and the egg packs they engage with most.</p>
      </div>

      {/* Most clicked packs */}
      <section className="mt-6">
        <h2 className="flex items-center gap-2 font-heading text-lg text-ink"><TrendingUp className="h-4 w-4 text-brand" /> Most Clicked Packs</h2>
        {clicks.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">No product clicks recorded yet. Interest will appear here as customers browse.</p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {clicks.slice(0, 6).map((c) => (
              <div key={c.product_id} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={`click-stat-${c.product_id}`}>
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand"><Egg className="h-5 w-5" /></span>
                  <span className="flex items-center gap-1 text-sm font-semibold text-ink"><MousePointerClick className="h-4 w-4 text-brand" /> {c.clicks}</span>
                </div>
                <p className="mt-3 font-heading text-base text-ink line-clamp-1">{c.product_name}</p>
                <p className="text-xs text-ink-muted">{c.unique_customers} customer{c.unique_customers === 1 ? "" : "s"} interested</p>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-cream-alt">
                  <div className="h-full rounded-full bg-brand" style={{ width: `${maxClicks ? (c.clicks / maxClicks) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Customer table */}
      <section className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-lg text-ink"><Users className="h-4 w-4 text-brand" /> All Customers ({customers.length})</h2>
        {customers.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-border bg-white p-12 text-center">
            <Users className="mx-auto h-10 w-10 text-ink-muted" />
            <p className="mt-4 font-heading text-lg text-ink">No customers yet</p>
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-2xl border border-border/60 bg-white shadow-ambient">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-cream-alt/60 text-xs uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Mobile</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3">Orders</th>
                    <th className="px-4 py-3">Spent</th>
                    <th className="px-4 py-3">Most Clicked Packs</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {customers.map((c) => (
                    <tr key={c.id} data-testid={`admin-customer-${c.mobile}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 font-semibold text-ink">{c.name?.[0]?.toUpperCase() || "C"}</span>
                          <span className="font-medium text-ink">{c.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-ink-soft"><Phone className="h-3.5 w-3.5" /> {c.mobile}</span>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "—"}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-ink"><ShoppingBag className="h-3.5 w-3.5 text-sage" /> {c.order_count}</span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-ink">{rupee(c.total_spent)}</td>
                      <td className="px-4 py-3">
                        {c.top_products && c.top_products.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {c.top_products.map((p, i) => (
                              <span key={i} className={`rounded-full px-2.5 py-1 text-xs font-medium ${i === 0 ? "bg-brand/20 text-ink" : "bg-cream-alt text-ink-soft"}`}>
                                {p.product_name} · {p.clicks}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-ink-muted">No clicks yet</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="icon" onClick={() => remove(c)} className="text-destructive hover:bg-destructive/10" data-testid={`delete-customer-${c.mobile}`} aria-label="Remove customer">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
